import React, { useCallback, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, FormControlLabel, Switch } from "@mui/material";
import Map, { TravelMarkerType } from "src/atoms/Map";
import { useLazyQuery } from "@apollo/client";
import { positionByExternalId,
  TVehiclePositionByExternalIdQueries
} from "src/store/graphql/groups/travel/query.graphql";
import {
  ConcretingType,
  ETravelStatus,
  Maybe,
  TravelQueriesGetPositionByExternalIdArgs,
  TravelType } from "@store/graphql/helpers/types.graphql";
import markerHandler, { MarkersVisualizationTypes } from "src/helpers/markerHandler";
import useTranslation from "@languages/useTranslation";
import { BaseCardMessages } from "src/languages/interfaces";
import Styled, { PurpleSwitch } from "./styles";

interface TravelsResumeDialogProps {
    isOpen: boolean;
    onClose: () => void;
    concreting: ConcretingType | undefined;

}

const TravelsResumeDialog: React.FC<TravelsResumeDialogProps> = ({ isOpen, onClose, concreting }) => {
  const [travelMarkers, setTravelMarkers] = useState<TravelMarkerType[]>([]);
  const [markersVisualization, setMarkersVisualization] = useState<MarkersVisualizationTypes>({
    name: false,
    licensePlate: false
  });

  const { t, i18n } = useTranslation();

  const [getPositionByExternalId] = useLazyQuery<
  TVehiclePositionByExternalIdQueries,
  TravelQueriesGetPositionByExternalIdArgs
>(positionByExternalId(["lastSendDate", "lastLatitude", "lastLongitude"]));

  const handleToogleMarkerVisualization = (visualizationToToogle: keyof MarkersVisualizationTypes) => {
    if (visualizationToToogle === "name") {
      setMarkersVisualization(((prevState) => ({
        name: !prevState.name,
        licensePlate: prevState.licensePlate
      })));
    } else {
      setMarkersVisualization(((prevState) => ({
        name: prevState.name,
        licensePlate: !prevState.licensePlate
      })));
    }
  };

  const isDisplayedTravelStatus = (travel: Maybe<TravelType>) => (
    travel?.status === ETravelStatus.IN_TRANSIT
        || travel?.status === ETravelStatus.DISCHARGING
        || travel?.status === ETravelStatus.IN_CONSTRUCTION
  );

  const getTravelsInfo = async (travels: TravelType[]) => {

    if (travels.length > 0) {
      const queryResponses: any = [];

      for await (const travel of travels) {
        if (travel) {
          const travelResult = await new Promise((resolve) => {
            getPositionByExternalId({
              variables: {
                externalId: travel.fleetExternalId ?? ""
              }
            }).then((response) => {
              if (response.data?.travel.getPositionByExternalId.result?.lastSendDate) {
                resolve({
                  travel,
                  marker: {
                    position: {
                      lat: Number.parseFloat(response.data?.travel.getPositionByExternalId.result?.lastLatitude ?? "0"),
                      lng: Number.parseFloat(response.data?.travel.getPositionByExternalId.result?.lastLongitude ?? "0")
                    },
                    icon: {
                      url: markerHandler.getMarkerAccordingTravel(travel, markersVisualization),
                      scaledSize: new google.maps.Size(47.5, 47.5)
                    }
                  },
                  lastSendDate: response.data?.travel.getPositionByExternalId.result?.lastSendDate
                });
              } else {
                resolve(null);
              }
            });
          });

          if (travelResult) {
            queryResponses.push(travelResult);
          }
        }
      }

      return queryResponses;
    }

    return [];
  };

  useEffect(() => {
    if (concreting) {
      const runningTravels = concreting.travels?.filter((travel) => isDisplayedTravelStatus(travel)) as TravelType[];

      getTravelsInfo(runningTravels ?? []).then((response) => {
        setTravelMarkers(response);
      });

    }
  }, [concreting]);

  useEffect(() => {
    let iconSize = 47.5;

    if (markersVisualization.name) {
      iconSize = 88;
    }

    if (markersVisualization.licensePlate) {
      iconSize = 120;
    }

    setTravelMarkers((prevState) => (prevState.map((travelMarker) => {
      if (markersVisualization.licensePlate || markersVisualization.name) {
        return (
          {
            travel: travelMarker.travel,
            marker: {
              ...travelMarker.marker,
              icon: {
                url: markerHandler.getMarkerAccordingTravel(travelMarker.travel, markersVisualization),
                scaledSize: new google.maps.Size(iconSize, iconSize),
                anchor: new google.maps.Point(16.5, 47.5)
              }
            }
          }
        );
      }

      return (
        {
          travel: travelMarker.travel,
          marker: {
            ...travelMarker.marker,
            icon: {
              url: markerHandler.getMarkerAccordingTravel(travelMarker.travel, markersVisualization),
              scaledSize: new google.maps.Size(iconSize, iconSize)
            }
          }
        }
      );
    })));
  }, [markersVisualization]);

  return (
    <Dialog open={isOpen} onClose={onClose}>

      <Styled.MapContainer>
        <Map travelMarkers={travelMarkers} hasInfowindow />
      </Styled.MapContainer>
      <DialogActions>
        <Styled.DialogActions>
          <div>
            <FormControlLabel
              control={(
                <PurpleSwitch
                  checked={markersVisualization.name}
                  onChange={() => handleToogleMarkerVisualization("name")}
                />
)}
              label={t(BaseCardMessages.mapTextNameLabel)}
            />
            <FormControlLabel
              control={(
                <PurpleSwitch
                  checked={markersVisualization.licensePlate}
                  onChange={() => handleToogleMarkerVisualization("licensePlate")}
                />
)}
              label={t(BaseCardMessages.travelPlateText)}
            />
          </div>

          <Button onClick={onClose}>Fechar</Button>
        </Styled.DialogActions>
      </DialogActions>
    </Dialog>
  );
};

export default TravelsResumeDialog;